import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Send, Eye, Users, RefreshCw, History, CheckCircle2, Circle, ExternalLink } from "lucide-react";

/** Attorney Digest — Preview / Summary / History.
 *  - Preview (cards)  : per-attorney rendered HTML body preview
 *  - Summary (table)  : attorney → # decisions → contract chips
 *  - History (batches): last 20 sent batches with per-decision engagement
 *                       (did the assigned attorney touch the contract since
 *                       sent_at? computed server-side from activity feed)
 *  "Send digest now" flushes /renewals/attorney-digest/send. */
export default function AttorneyDigestPreviewModal({ open, onClose, onSent }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [history, setHistory] = useState(null);
  const [busy, setBusy] = useState(false);
  const [historyBusy, setHistoryBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [tab, setTab] = useState("cards"); // cards | summary | history

  const loadPreview = () => {
    setBusy(true);
    return api.get("/renewals/attorney-digest/preview")
      .then(r => setData(r.data))
      .catch(e => toast.error(e?.response?.data?.detail || "Preview failed"))
      .finally(() => setBusy(false));
  };
  const loadHistory = () => {
    setHistoryBusy(true);
    return api.get("/renewals/attorney-digest/history?limit=20")
      .then(r => setHistory(r.data))
      .catch(e => toast.error(e?.response?.data?.detail || "History failed"))
      .finally(() => setHistoryBusy(false));
  };
  useEffect(() => { if (open) { loadPreview(); setHistory(null); } }, [open]);
  useEffect(() => { if (open && tab === "history" && history === null && !historyBusy) loadHistory(); }, [open, tab]);

  const send = async () => {
    if (!window.confirm(`Send digest to ${data.attorneys} attorney(s) now? This flushes ${data.total_pending} pending decision(s).`)) return;
    setSending(true);
    try {
      const r = await api.post("/renewals/attorney-digest/send");
      const failCount = (r.data.failed || []).length;
      if (failCount > 0) {
        toast.warning(`Digest sent to ${r.data.sent}. ${failCount} failed — check server logs.`);
      } else {
        toast.success(`Digest sent to ${r.data.sent} attorney(s)`);
      }
      onSent && onSent();
      // Refresh both panes; leave the modal open so leadership can see history.
      await loadPreview();
      setHistory(null); loadHistory();
      setTab("history");
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  if (!open) return null;
  const empty = data && data.total_pending === 0;

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4"
         data-testid="attorney-digest-preview-modal">
      <div className="bg-white rounded-md max-w-4xl w-full max-h-[90vh] flex flex-col shadow-xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
          <Eye className="w-4 h-4 text-[#005289]" />
          <div>
            <div className="font-bold text-sm text-slate-900">Attorney Digest</div>
            <div className="text-[11px] text-slate-500">
              {busy ? "Loading…" :
                data ? `${data.total_pending} pending decision(s) across ${data.attorneys} attorney(s)` : "—"}
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={tab === "history" ? loadHistory : loadPreview}
                    disabled={busy || historyBusy}
                    data-testid="digest-preview-refresh"
                    className="h-8 px-2 text-xs border border-slate-200 rounded hover:bg-slate-50 flex items-center gap-1 disabled:opacity-50">
              <RefreshCw className={`w-3 h-3 ${(busy || historyBusy) ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button onClick={send} disabled={sending || busy || empty}
                    data-testid="digest-preview-send"
                    className="h-8 px-3 text-xs font-semibold rounded bg-[#00AE42] text-white flex items-center gap-1.5 disabled:opacity-40">
              <Send className="w-3 h-3" /> {sending ? "Sending…" : "Send digest now"}
            </button>
            <button onClick={onClose} data-testid="digest-preview-close"
                    className="text-slate-400 hover:text-slate-700">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {!busy && data && (
          <div className="px-5 pt-3 flex items-center gap-1 text-xs border-b border-slate-100">
            <button onClick={() => setTab("cards")} disabled={empty}
                    data-testid="digest-preview-tab-cards"
                    className={`px-3 py-1.5 rounded-t border-b-2 disabled:opacity-40 ${tab === "cards" ? "border-[#005289] font-bold text-[#005289]" : "border-transparent text-slate-500"}`}>
              Per-attorney emails
            </button>
            <button onClick={() => setTab("summary")} disabled={empty}
                    data-testid="digest-preview-tab-summary"
                    className={`px-3 py-1.5 rounded-t border-b-2 disabled:opacity-40 ${tab === "summary" ? "border-[#005289] font-bold text-[#005289]" : "border-transparent text-slate-500"}`}>
              Summary table
            </button>
            <button onClick={() => setTab("history")}
                    data-testid="digest-preview-tab-history"
                    className={`px-3 py-1.5 rounded-t border-b-2 flex items-center gap-1 ${tab === "history" ? "border-[#005289] font-bold text-[#005289]" : "border-transparent text-slate-500"}`}>
              <History className="w-3 h-3" /> History
            </button>
          </div>
        )}

        <div className="flex-1 overflow-auto p-4">
          {busy && <div className="text-sm text-slate-500 text-center py-8">Loading preview…</div>}

          {!busy && tab !== "history" && empty && (
            <div className="text-sm text-slate-500 text-center py-10 border border-dashed border-slate-200 rounded"
                 data-testid="digest-preview-empty">
              No pending renewal decisions to digest. When Legal records new
              "Renew / Renegotiate / Let lapse" decisions in <b>digest mode</b>,
              they'll queue up here for the next flush.
            </div>
          )}

          {!busy && data && !empty && tab === "cards" && (
            <div className="space-y-4">
              {data.cards.map(card => (
                <div key={card.attorney_id} className="border border-slate-200 rounded"
                     data-testid={`digest-preview-card-${card.attorney_id}`}>
                  <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
                    <Users className="w-3.5 h-3.5 text-slate-500" />
                    <div>
                      <div className="font-bold text-sm text-slate-900">{card.attorney_name}</div>
                      <div className="text-[11px] text-slate-500">{card.attorney_email || "— no email —"}</div>
                    </div>
                    <span className="ml-auto text-[10px] font-bold px-2 py-1 bg-[#005289] text-white rounded">
                      {card.count} decision{card.count === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div className="px-4 py-3">
                    <div className="text-[11px] text-slate-500 mb-1">Subject</div>
                    <div className="text-sm font-semibold text-slate-900 mb-3">{card.subject}</div>
                    <div className="text-[11px] text-slate-500 mb-1">Rendered body</div>
                    <div className="border border-slate-200 rounded bg-white p-3 text-sm max-h-64 overflow-auto"
                         dangerouslySetInnerHTML={{ __html: card.body_html }} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {!busy && data && !empty && tab === "summary" && (
            <table className="hcd-table" data-testid="digest-preview-summary-table">
              <thead>
                <tr>
                  <th>Attorney</th><th>Email</th><th className="num"># decisions</th><th>Contracts</th>
                </tr>
              </thead>
              <tbody>
                {data.summary.map(s => (
                  <tr key={s.attorney_id || "_none"} data-testid={`digest-summary-row-${s.attorney_id || "none"}`}>
                    <td className="font-semibold">{s.attorney_name}</td>
                    <td className="text-xs text-slate-500">{s.attorney_email || "—"}</td>
                    <td className="num font-mono">{s.count}</td>
                    <td className="text-xs">
                      {s.contracts.map(c => (
                        <span key={c.contract_number} className="inline-block mr-2 mb-1 px-2 py-0.5 rounded bg-slate-100 border border-slate-200">
                          #{c.contract_number} · {c.vendor} · <b>{(c.decision || "").replace("_", " ")}</b>
                        </span>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {!busy && tab === "history" && (
            <DigestHistoryPanel history={history} busy={historyBusy} nav={nav} />
          )}
        </div>
      </div>
    </div>
  );
}

function DigestHistoryPanel({ history, busy, nav }) {
  if (busy || history === null) {
    return <div className="text-sm text-slate-500 text-center py-8" data-testid="digest-history-loading">Loading history…</div>;
  }
  const batches = history?.batches || [];
  if (batches.length === 0) {
    return (
      <div className="text-sm text-slate-500 text-center py-10 border border-dashed border-slate-200 rounded"
           data-testid="digest-history-empty">
        No digest batches have been sent yet. History appears here after the
        first "Send digest now" or overnight cron run.
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="digest-history-panel">
      {batches.map(b => {
        const when = b.sent_at ? new Date(b.sent_at) : null;
        return (
          <div key={b.batch_id} className="border border-slate-200 rounded"
               data-testid={`digest-history-batch-${b.batch_id}`}>
            <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex items-center gap-3 flex-wrap">
              <History className="w-3.5 h-3.5 text-slate-500" />
              <div>
                <div className="font-bold text-sm text-slate-900">
                  {when ? when.toLocaleString() : "—"}
                </div>
                <div className="text-[10px] text-slate-500 font-mono">
                  batch {b.batch_id} · sent by {b.sent_by || "—"}
                </div>
              </div>
              <div className="ml-auto flex items-center gap-2 text-[11px]">
                <span className="px-2 py-0.5 rounded bg-slate-200 font-semibold">
                  {b.total_decisions} decisions
                </span>
                <span className={`px-2 py-0.5 rounded font-semibold ${b.engagement_rate >= 66 ? "bg-emerald-100 text-emerald-800" : b.engagement_rate >= 33 ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800"}`}
                      data-testid={`digest-history-engagement-${b.batch_id}`}>
                  {b.engagement_rate}% engaged ({b.total_engaged}/{b.total_decisions})
                </span>
              </div>
            </div>
            <div className="p-3 space-y-2">
              {b.attorneys.map(a => (
                <div key={(a.attorney_id || "none") + b.batch_id}
                     className="border border-slate-100 rounded p-2"
                     data-testid={`digest-history-atty-${a.attorney_id || "none"}`}>
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <span className="text-xs font-bold text-slate-900">{a.attorney_name}</span>
                    <span className="text-[10px] text-slate-500">{a.attorney_email || "—"}</span>
                    <span className="ml-auto text-[10px] font-mono text-slate-500">
                      {a.engaged_count}/{a.count} touched since sent
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                    {a.rows.map(r => (
                      <button
                        key={r.contract_id}
                        onClick={() => r.contract_id && nav(`/contract/${r.contract_id}`)}
                        data-testid={`digest-history-row-${r.contract_number}`}
                        className="text-left px-2 py-1.5 rounded border border-slate-100 hover:border-[#005289] hover:bg-slate-50 flex items-center gap-2 text-xs"
                      >
                        {r.engaged
                          ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                          : <Circle className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <div className="truncate">
                            <span className="font-mono text-slate-500">#{r.contract_number}</span>
                            <span className="mx-1 text-slate-400">·</span>
                            <span className="font-semibold">{r.vendor}</span>
                          </div>
                          <div className="text-[10px] text-slate-500 truncate">
                            <b>{(r.decision || "").replace("_", " ")}</b>
                            {r.engaged_at && <span className="ml-2">· touched {new Date(r.engaged_at).toLocaleDateString()}</span>}
                          </div>
                        </div>
                        <ExternalLink className="w-3 h-3 text-slate-300" />
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
